import React from 'react';

const STORAGE_KEY = 'tradournal_ma_watchlist';
const DISMISSED_KEY = 'tradournal_ma_dismissed';

const DEFAULT_LARGE_CAPS = [
  'AAPL','MSFT','NVDA','GOOGL','AMZN','META','TSLA','BRK-B','JPM','V',
  'UNH','XOM','LLY','AVGO','JNJ','PG','MA','HD','MRK','COST',
  'ABBV','CVX','KO','PEP','WMT','BAC','CRM','NFLX','AMD','ADBE',
  'DIS','INTC','ORCL','CSCO','TMO','ABT','MCD','NKE','TXN','QCOM',
];

function loadStorage(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) || fallback; } catch { return fallback; }
}

function fmt(v) {
  if (v == null || isNaN(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pctColor(pct, C) {
  if (pct == null) return C.sub;
  const abs = Math.abs(pct);
  if (abs <= 0.5) return '#f97316';
  if (abs <= 1.5) return '#eab308';
  return pct > 0 ? C.greenBright : C.redBright;
}

const MA_COLORS = { 20: '#2b8cff', 50: '#a855f7', 200: '#f97316' };

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

async function sendPush(userId, title, body, tag) {
  if (!userId) return;
  try {
    await fetch('/api/push', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'send', userId, title, body, tag }),
    });
  } catch {}
}

// ── Breadth stat tile ─────────────────────────────────────────────────────
function BreadthTile({ label, above, total, color, C }) {
  const pct = total > 0 ? Math.round((above / total) * 100) : 0;
  const below = total - above;
  const isDark = C.card !== '#ffffff';
  const bg = isDark
    ? hexToRgba(color, 0.18)
    : hexToRgba(color, 0.35);
  const numColor = isDark ? color : color;
  return (
    <div style={{
      flex: 1, minWidth: 180,
      background: bg,
      border: `2px solid ${color}`,
      borderRadius: 12, padding: '18px 20px',
      boxShadow: `0 4px 16px ${hexToRgba(color, 0.35)}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 10 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 40, fontWeight: 900, color: numColor, lineHeight: 1 }}>{above}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.text, opacity: 0.5 }}>/ {total}</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: numColor, marginLeft: 4 }}>{pct}%</span>
      </div>
      {/* Bar */}
      <div style={{ height: 10, borderRadius: 5, background: hexToRgba(color, 0.25), overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 5, background: color, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.green }}>▲ Above {above}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.red }}>▼ Below {below}</span>
      </div>
    </div>
  );
}

// ── MA cell showing direction + actual % ─────────────────────────────────
function MaCell({ pct, C }) {
  if (pct == null) return <span style={{ color: C.sub, fontSize: 13 }}>—</span>;
  const above = pct > 0;
  const gc = C.greenBright, rc = C.redBright;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 6, fontSize: 10,
        background: above ? `${gc}30` : `${rc}28`,
        color: above ? gc : rc,
        border: `1px solid ${above ? gc : rc}60`,
        fontWeight: 900, flexShrink: 0,
      }}>
        {above ? '▲' : '▼'}
      </span>
      <span style={{ fontSize: 13, fontWeight: 800, color: above ? gc : rc }}>
        {above ? '+' : ''}{pct.toFixed(2)}%
      </span>
    </div>
  );
}

// ── 52w High cell ─────────────────────────────────────────────────────────
function AthCell({ pctFromHigh, high52w, C }) {
  if (pctFromHigh == null) return <span style={{ color: C.sub, fontSize: 13 }}>—</span>;
  const abs = Math.abs(pctFromHigh);
  let color, bg, label;
  if (abs <= 1)       { color = C.greenBright; bg = `${C.greenBright}30`; label = 'ATH'; }
  else if (abs <= 5)  { color = C.greenBright; bg = `${C.greenBright}20`; label = `${pctFromHigh.toFixed(1)}%`; }
  else if (abs <= 15) { color = C.blueBright;  bg = `${C.blueBright}22`;  label = `${pctFromHigh.toFixed(1)}%`; }
  else                { color = C.sub;         bg = `${C.sub}18`;         label = `${pctFromHigh.toFixed(1)}%`; }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: bg, color, border: `1px solid ${color}60`, width: 'fit-content' }}>
        {abs <= 1 && <span style={{ fontSize: 8 }}>●</span>}
        {label}
      </span>
      <span style={{ fontSize: 11, color: C.sub, fontFamily: 'monospace' }}>${fmt(high52w)}</span>
    </div>
  );
}

export default function MAlertsWindow({ C, cardStyle, inp, userId }) {
  const [symbols, setSymbols]       = React.useState(() => loadStorage(STORAGE_KEY, DEFAULT_LARGE_CAPS));
  const [inputSym, setInputSym]     = React.useState('');
  const [threshold, setThreshold]   = React.useState('1.5');
  const [data, setData]             = React.useState({ hits: [], all: [] });
  const [loading, setLoading]       = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState(null);
  const [dismissed, setDismissed]   = React.useState(() => loadStorage(DISMISSED_KEY, []));
  const [view, setView]             = React.useState('alerts');
  const [showSymbolEditor, setShowSymbolEditor] = React.useState(false);
  const [athSort, setAthSort]       = React.useState('pctFromHigh');
  const [athFilter, setAthFilter]   = React.useState('all');

  React.useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(symbols)); }, [symbols]);
  React.useEffect(() => { localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed)); }, [dismissed]);

  const scan = React.useCallback(async () => {
    if (symbols.length === 0) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/gap-scanner?mode=ma&symbols=${encodeURIComponent(symbols.join(','))}&threshold=${threshold}`);
      const json = await res.json();
      if (json.hits !== undefined) {
        setData(json);
        setLastUpdated(new Date());
        (json.hits || []).forEach(h => {
          h.hits.forEach(ma => {
            const key = `${h.symbol}-ma${ma.ma}-${new Date().toDateString()}`;
            if (!dismissed.includes(key)) {
              const title = `MA Alert: ${h.symbol}`;
              const body  = `${h.symbol} is within ${Math.abs(ma.pct).toFixed(2)}% of its ${ma.ma}-day MA ($${fmt(ma.value)})`;
              if (Notification.permission === 'granted') new Notification(title, { body, icon: '/favicon.ico' });
              sendPush(userId, title, body, `ma-${h.symbol}-${ma.ma}`);
            }
          });
        });
      }
    } catch {}
    setLoading(false);
  }, [symbols, threshold, dismissed, userId]);

  React.useEffect(() => {
    scan();
    const id = setInterval(scan, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [scan]);

  const addSymbol = () => {
    const s = inputSym.trim().toUpperCase();
    if (!s || symbols.includes(s)) { setInputSym(''); return; }
    setSymbols(prev => [...prev, s]);
    setInputSym('');
  };
  const removeSymbol   = s => setSymbols(prev => prev.filter(x => x !== s));
  const dismiss        = (symbol, ma) => setDismissed(prev => [...prev, `${symbol}-ma${ma}-${new Date().toDateString()}`]);
  const isDismissed    = (symbol, ma) => dismissed.includes(`${symbol}-ma${ma}-${new Date().toDateString()}`);

  const activeHits = (data.hits || [])
    .map(h => ({ ...h, hits: h.hits.filter(ma => !isDismissed(h.symbol, ma.ma)) }))
    .filter(h => h.hits.length > 0);

  const all = data.all || [];

  const athRows = React.useMemo(() => {
    let rows = [...all].filter(r => r.pctFromHigh != null);
    if (athFilter === 'above') rows = rows.filter(r => r.pct20 > 0 && r.pct50 > 0 && r.pct200 > 0);
    if (athFilter === 'below') rows = rows.filter(r => r.pct20 < 0 && r.pct50 < 0 && r.pct200 < 0);
    if (athSort === 'pctFromHigh') rows.sort((a, b) => b.pctFromHigh - a.pctFromHigh);
    if (athSort === 'price')       rows.sort((a, b) => b.price - a.price);
    if (athSort === 'pct20')       rows.sort((a, b) => (b.pct20 ?? -999) - (a.pct20 ?? -999));
    if (athSort === 'pct50')       rows.sort((a, b) => (b.pct50 ?? -999) - (a.pct50 ?? -999));
    if (athSort === 'pct200')      rows.sort((a, b) => (b.pct200 ?? -999) - (a.pct200 ?? -999));
    return rows;
  }, [all, athSort, athFilter]);

  const breadth = React.useMemo(() => {
    const v = all.filter(r => r.price);
    return {
      total: v.length,
      above20:  v.filter(r => r.pct20  > 0).length,
      above50:  v.filter(r => r.pct50  > 0).length,
      above200: v.filter(r => r.pct200 > 0).length,
      nearHigh: v.filter(r => r.pctFromHigh != null && Math.abs(r.pctFromHigh) <= 5).length,
      allAbove: v.filter(r => r.pct20 > 0 && r.pct50 > 0 && r.pct200 > 0).length,
    };
  }, [all]);

  const lbl = { color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' };

  const VIEWS = [
    ['alerts', `Alerts (${activeHits.length})`],
    ['ath',    `ATH & Breadth (${all.length})`],
    ['all',    `All Data (${all.length})`],
  ];

  const COL = '100px 100px 160px 1fr 1fr 1fr'; // grid template

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Header card ── */}
      <div style={{ ...cardStyle, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ color: C.text, fontWeight: 800, fontSize: 16, letterSpacing: '-0.3px' }}>MA Alert Scanner</div>
            <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>
              {loading ? 'Scanning…' : lastUpdated
                ? `Last scan ${lastUpdated.toLocaleTimeString()} · auto-refreshes every 5 min`
                : 'Monitors large caps for MA touches and new highs'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {activeHits.length > 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 6, padding: '4px 12px' }}>
                {activeHits.length} alert{activeHits.length !== 1 ? 's' : ''}
              </span>
            )}
            <button onClick={() => { if (Notification.permission === 'default') Notification.requestPermission(); scan(); }}
              style={{ padding: '8px 16px', background: loading ? C.card2 : `${C.blue}20`, border: `1px solid ${loading ? C.border : C.blue}40`, color: loading ? C.sub : C.blueBright, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              {loading ? 'Scanning…' : 'Scan Now'}
            </button>
            <button onClick={() => setShowSymbolEditor(v => !v)}
              style={{ padding: '8px 16px', background: C.card2, border: `1px solid ${C.border}`, color: C.sub, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              {showSymbolEditor ? 'Hide List' : `Watchlist (${symbols.length})`}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...lbl }}>Alert proximity</span>
            <select value={threshold} onChange={e => setThreshold(e.target.value)}
              style={{ ...inp, padding: '6px 10px', fontSize: 12, width: 'auto' }}>
              <option value="0.5">0.5%</option>
              <option value="1.0">1.0%</option>
              <option value="1.5">1.5% (default)</option>
              <option value="2.0">2.0%</option>
              <option value="3.0">3.0%</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[20, 50, 200].map(ma => (
              <span key={ma} style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: `${MA_COLORS[ma]}18`, border: `1px solid ${MA_COLORS[ma]}40`, color: MA_COLORS[ma] }}>
                {ma}-Day MA
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Symbol editor ── */}
      {showSymbolEditor && (
        <div style={{ ...cardStyle, padding: '18px 24px' }}>
          <div style={{ ...lbl, marginBottom: 12 }}>Watchlist · {symbols.length} symbols</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input value={inputSym} onChange={e => setInputSym(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && addSymbol()} placeholder="Add ticker e.g. AAPL"
              style={{ ...inp, flex: 1 }} />
            <button onClick={addSymbol}
              style={{ padding: '9px 20px', background: C.blue, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
              + Add
            </button>
            <button onClick={() => setSymbols(DEFAULT_LARGE_CAPS)}
              style={{ padding: '9px 16px', background: C.card2, border: `1px solid ${C.border}`, color: C.sub, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              Reset
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {symbols.map(s => (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: C.card2, border: `1px solid ${C.border}`, color: C.text }}>
                {s}
                <button onClick={() => removeSymbol(s)} style={{ background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 15, padding: 0, lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── View tabs ── */}
      <div style={{ display: 'flex', gap: 6 }}>
        {VIEWS.map(([v, label]) => (
          <button key={v} onClick={() => setView(v)} style={{
            padding: '8px 18px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
            border: `1px solid ${view === v ? C.blue : C.border}`,
            background: view === v ? `${C.blue}18` : C.card2,
            color: view === v ? C.blueBright : C.sub,
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ══════════════════ ALERTS VIEW ══════════════════ */}
      {view === 'alerts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {activeHits.length === 0 ? (
            <div style={{ ...cardStyle, padding: '50px 20px', textAlign: 'center', color: C.sub, fontSize: 14 }}>
              {loading ? 'Scanning moving averages…' : `No stocks within ${threshold}% of their MA right now.`}
            </div>
          ) : activeHits.map(h => (
            <div key={h.symbol} style={{ ...cardStyle, padding: '18px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 50, height: 50, borderRadius: 12, background: `${C.blue}18`, border: `1px solid ${C.blue}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 12, color: C.blueBright }}>
                    {h.symbol}
                  </div>
                  <div>
                    <div style={{ color: C.text, fontWeight: 800, fontSize: 20, fontFamily: 'monospace' }}>${fmt(h.price)}</div>
                    <div style={{ color: C.sub, fontSize: 11, marginTop: 2 }}>Current price</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {h.hits.map(ma => (
                    <div key={ma.ma} style={{ padding: '10px 16px', borderRadius: 12, background: `${MA_COLORS[ma.ma]}12`, border: `1px solid ${MA_COLORS[ma.ma]}40`, minWidth: 120 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: MA_COLORS[ma.ma] }}>{ma.ma}-Day MA</span>
                        <button onClick={() => dismiss(h.symbol, ma.ma)} style={{ background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1, opacity: 0.5 }}>×</button>
                      </div>
                      <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 2 }}>${fmt(ma.value)}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: pctColor(ma.pct, C) }}>
                        {ma.pct >= 0 ? '+' : ''}{ma.pct.toFixed(2)}% from MA
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 20, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                {[{ ma: 20, v: h.ma20 }, { ma: 50, v: h.ma50 }, { ma: 200, v: h.ma200 }].map(({ ma, v }) => (
                  <div key={ma}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: MA_COLORS[ma] }}>MA{ma}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>{v ? `$${fmt(v)}` : '—'}</div>
                  </div>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                  <a href={`https://finance.yahoo.com/chart/${h.symbol}`} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, color: C.blue, textDecoration: 'none', fontWeight: 700 }}>
                    View Chart →
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ══════════════════ ATH & BREADTH VIEW ══════════════════ */}
      {view === 'ath' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Breadth tiles */}
          {breadth.total > 0 && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <BreadthTile label="Above 20 DMA"  above={breadth.above20}  total={breadth.total} color={MA_COLORS[20]}  C={C} />
              <BreadthTile label="Above 50 DMA"  above={breadth.above50}  total={breadth.total} color={MA_COLORS[50]}  C={C} />
              <BreadthTile label="Above 200 DMA" above={breadth.above200} total={breadth.total} color={MA_COLORS[200]} C={C} />
              {/* Summary tile */}
              <div style={{
                flex: 1, minWidth: 160,
                background: C.card !== '#ffffff' ? hexToRgba(C.greenBright, 0.18) : hexToRgba(C.greenBright, 0.35),
                border: `2px solid ${C.greenBright}`, borderRadius: 12, padding: '18px 20px',
                boxShadow: `0 4px 16px ${hexToRgba(C.greenBright, 0.35)}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.greenBright, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 10 }}>
                  Near 52w High
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}>
                  <span style={{ fontSize: 40, fontWeight: 900, color: C.greenBright, lineHeight: 1 }}>{breadth.nearHigh}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.text, opacity: 0.5 }}>/ {breadth.total}</span>
                </div>
                <div style={{ fontSize: 12, color: C.text, opacity: 0.6 }}>within 5% of high</div>
                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: C.greenBright }}>
                  {breadth.allAbove} above all 3 MAs
                </div>
              </div>
            </div>
          )}

          {/* ATH table */}
          <div style={{ ...cardStyle, padding: '20px 24px' }}>
            {athRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: C.sub, fontSize: 14 }}>
                {loading ? 'Loading data…' : 'Click "Scan Now" to load.'}
              </div>
            ) : (
              <>
                {/* Table controls */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[['all', 'All'], ['above', '▲ All Above MAs'], ['below', '▼ All Below MAs']].map(([v, label]) => (
                      <button key={v} onClick={() => setAthFilter(v)} style={{
                        padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                        border: `1px solid ${athFilter === v ? C.blue : C.border}`,
                        background: athFilter === v ? `${C.blue}18` : C.card2,
                        color: athFilter === v ? C.blueBright : C.sub,
                      }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                    <span style={{ ...lbl }}>Sort by</span>
                    <select value={athSort} onChange={e => setAthSort(e.target.value)}
                      style={{ ...inp, padding: '6px 12px', fontSize: 12, width: 'auto' }}>
                      <option value="pctFromHigh">Near 52w High</option>
                      <option value="pct20">% from 20 DMA</option>
                      <option value="pct50">% from 50 DMA</option>
                      <option value="pct200">% from 200 DMA</option>
                      <option value="price">Price</option>
                    </select>
                  </div>
                </div>

                {/* Column headers */}
                <div style={{ display: 'grid', gridTemplateColumns: COL, gap: 12, padding: '8px 14px 12px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                  {[
                    { label: 'Symbol', col: null },
                    { label: 'Price',  col: 'price' },
                    { label: '52-Week High', col: 'pctFromHigh' },
                    { label: '20 DMA',  col: 'pct20',  color: MA_COLORS[20] },
                    { label: '50 DMA',  col: 'pct50',  color: MA_COLORS[50] },
                    { label: '200 DMA', col: 'pct200', color: MA_COLORS[200] },
                  ].map(({ label, col, color }) => (
                    <div key={label}
                      onClick={() => col && setAthSort(col)}
                      style={{ color: col && athSort === col ? (color || C.blueBright) : C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', cursor: col ? 'pointer' : 'default', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {label}
                      {col && athSort === col && <span style={{ fontSize: 9 }}>▼</span>}
                    </div>
                  ))}
                </div>

                {/* Rows */}
                {athRows.map((r, i) => {
                  const allAbove = r.pct20 > 0 && r.pct50 > 0 && r.pct200 > 0;
                  const allBelow = r.pct20 < 0 && r.pct50 < 0 && r.pct200 < 0;
                  const atHigh   = r.pctFromHigh != null && Math.abs(r.pctFromHigh) <= 1;
                  return (
                    <div key={r.symbol} style={{
                      display: 'grid', gridTemplateColumns: COL, gap: 12,
                      padding: '14px 14px', alignItems: 'center', borderRadius: 10,
                      background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.018)',
                      borderLeft: `3px solid ${allAbove ? '#00e5a3' : allBelow ? '#ff5c5c' : 'transparent'}`,
                      marginBottom: 1,
                    }}>
                      {/* Symbol */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 900, fontSize: 10, flexShrink: 0,
                          background: atHigh ? `${C.greenBright}28` : allAbove ? `${C.blueBright}22` : allBelow ? `${C.redBright}22` : C.card2,
                          border: `1px solid ${atHigh ? C.greenBright : allAbove ? C.blueBright : allBelow ? C.redBright : C.border}60`,
                          color: atHigh ? C.greenBright : allAbove ? C.blueBright : allBelow ? C.redBright : C.text,
                        }}>
                          {r.symbol}
                        </div>
                      </div>

                      {/* Price */}
                      <div style={{ fontWeight: 800, color: C.text, fontSize: 14, fontFamily: 'monospace' }}>
                        ${fmt(r.price)}
                      </div>

                      {/* 52w High */}
                      <AthCell pctFromHigh={r.pctFromHigh} high52w={r.high52w} C={C} />

                      {/* MA cells */}
                      <MaCell pct={r.pct20}  C={C} />
                      <MaCell pct={r.pct50}  C={C} />
                      <MaCell pct={r.pct200} C={C} />
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════ ALL DATA VIEW ══════════════════ */}
      {view === 'all' && (
        <div style={{ ...cardStyle, padding: '20px 24px' }}>
          {all.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: C.sub, fontSize: 14 }}>
              {loading ? 'Loading data…' : 'Click "Scan Now" to load MA data.'}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '90px 90px 100px 100px 100px 100px 100px 100px', gap: 10, padding: '8px 12px 12px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                {['Symbol','Price','MA 20','% from 20','MA 50','% from 50','MA 200','% from 200'].map(h => (
                  <div key={h} style={{ color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</div>
                ))}
              </div>
              {all.map((r, i) => (
                <div key={r.symbol} style={{ display: 'grid', gridTemplateColumns: '90px 90px 100px 100px 100px 100px 100px 100px', gap: 10, padding: '12px', alignItems: 'center', borderRadius: 8, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.018)' }}>
                  <div style={{ fontWeight: 800, color: C.text, fontSize: 13 }}>{r.symbol}</div>
                  <div style={{ fontWeight: 700, color: C.text, fontSize: 13, fontFamily: 'monospace' }}>${fmt(r.price)}</div>
                  <div style={{ color: C.sub, fontSize: 12 }}>{r.ma20 ? `$${fmt(r.ma20)}` : '—'}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: pctColor(r.pct20, C) }}>{r.pct20 != null ? `${r.pct20 >= 0 ? '+' : ''}${r.pct20.toFixed(2)}%` : '—'}</div>
                  <div style={{ color: C.sub, fontSize: 12 }}>{r.ma50 ? `$${fmt(r.ma50)}` : '—'}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: pctColor(r.pct50, C) }}>{r.pct50 != null ? `${r.pct50 >= 0 ? '+' : ''}${r.pct50.toFixed(2)}%` : '—'}</div>
                  <div style={{ color: C.sub, fontSize: 12 }}>{r.ma200 ? `$${fmt(r.ma200)}` : '—'}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: pctColor(r.pct200, C) }}>{r.pct200 != null ? `${r.pct200 >= 0 ? '+' : ''}${r.pct200.toFixed(2)}%` : '—'}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
